import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AuthShell } from "@/components/AuthShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { apiCall } from "@/lib/shopApi";
import { useApiError } from "@/lib/useApiError";

const NIET_GEBOUWD = [404, 405, 501];
const ONGELDIGE_TOKEN = [400, 410, 422];

const WachtwoordReset = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toMessage = useApiError();
  const token = searchParams.get("token") || "";

  const [wachtwoord, setWachtwoord] = useState("");
  const [herhaling, setHerhaling] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (event) => {
    event.preventDefault();
    setError("");
    if (wachtwoord.length < 8) {
      setError(t("auth.reset.tooShort"));
      return;
    }
    if (wachtwoord !== herhaling) {
      setError(t("auth.reset.mismatch"));
      return;
    }
    setSubmitting(true);
    try {
      await apiCall("POST", "/shop/auth/wachtwoord-reset", { body: { token, wachtwoord } });
      toast.success(t("auth.reset.success"));
      navigate("/login", { replace: true });
    } catch (err) {
      if (NIET_GEBOUWD.includes(err?.status)) setError(t("auth.reset.unavailable"));
      else if (ONGELDIGE_TOKEN.includes(err?.status)) setError(t("auth.reset.invalidToken"));
      else setError(toMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell title={t("auth.reset.title")} subtitle={t("auth.reset.subtitle")} testId="reset-password-page">
        {!token ? (
          <div className="space-y-5">
            <Alert variant="destructive" data-testid="reset-password-missing-token-alert">
              <AlertDescription>{t("auth.reset.missingToken")}</AlertDescription>
            </Alert>
            <Button asChild className="w-full" data-testid="reset-password-new-link-button">
              <Link to="/wachtwoord-vergeten">{t("auth.reset.newLink")}</Link>
            </Button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-5">
            {error && (
              <Alert variant="destructive" data-testid="reset-password-error-alert">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="reset-password">{t("auth.reset.password")}</Label>
              <Input
                id="reset-password"
                type="password"
                autoComplete="new-password"
                required
                value={wachtwoord}
                onChange={(e) => setWachtwoord(e.target.value)}
                data-testid="reset-password-input"
              />
              <p className="text-xs text-muted-foreground">{t("auth.reset.hint")}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reset-password-confirm">{t("auth.reset.confirm")}</Label>
              <Input
                id="reset-password-confirm"
                type="password"
                autoComplete="new-password"
                required
                value={herhaling}
                onChange={(e) => setHerhaling(e.target.value)}
                data-testid="reset-password-confirm-input"
              />
            </div>
            <Button type="submit" className="w-full" disabled={submitting} data-testid="reset-password-submit-button">
              {submitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <KeyRound className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              {submitting ? t("auth.reset.submitting") : t("auth.reset.submit")}
            </Button>
            <p className="text-center text-sm">
              <Link to="/login" className="font-medium text-primary hover:underline" data-testid="reset-password-login-link">
                {t("auth.reset.toLogin")}
              </Link>
            </p>
          </form>
        )}
    </AuthShell>
  );
};

export default WachtwoordReset;
